import React, { Suspense, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Route } from 'react-router-dom';

// Import fontawsome globally.
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

import Container from 'components/Container';
import SiteNotificationContainer from 'components/SiteNotifications';
import APModal from 'components/Modal/APModal';
import Header from 'components/Header';
import { Loading } from 'components/Loading';
import SiteErrorContainer from 'components/SiteErrors';
import LeadsEmailer from 'pages/LeadsEmailer';
import * as actions from './actions';
import { SET_SHOW_PAUSED_MODAL } from './constants';
import { PrivateRoutes as Routes } from './routes';
import SyncModal from './SyncModal/SyncModal';
import { ForbiddenModal } from './ForbiddenModal';

// Add icons to library for global use.
library.add(fas, fal, fab);

const App = () => {
  const dispatch = useDispatch();
  const { isAdmin } = useSelector((state) => state.global);
  const userInfo = useSelector((state) => state.global.userInfo);
  const userInfoErrors = useSelector((state) => state.global.userInfoErrors);
  const userInfoLoaded = useSelector((state) => state.global.userInfoLoaded);
  const isAuthenticated = useSelector((state) => state.global.isAuthenticated);
  const redirectUrl = useSelector((state) => state.global.redirect);
  const showPausedModal = useSelector((state) => state.global.showPausedModal);
  const menuPermissions = useSelector((state) => state.global.menuPermissions);
  const isForbidden = useSelector((state) => state.global.isForbidden);
  const {
    billing,
    loginWeddings,
    id: userId,
    integrationsEnabled: isIntegrationsEnabled,
  } = userInfo || {};
  const { planningSubscription: subscription } = billing || {};
  const { isTrial, isExpired, isPromo } = subscription || {};
  const isTrialEnded = subscription && isTrial && isExpired;
  const isPromoEnded = subscription && isPromo && isExpired;
  // isCancel is if user canceled account themselves. isDisabled is if payment failed multiple times.
  const { isCancel, isPaused, isDisabled } = userInfo || {};
  const isUserPaused = isPaused;
  const { isDelinquent } = billing || {};
  // account is churned if account is ended, but not is canceled.
  const isChurned = subscription?.isEnded && !isCancel;
  const isOrgOwner = userInfo.id === userInfo.organization?.ownerId;
  const { hasUnpausedProjects } = userInfo.personas || {};
  const hasActiveWeddings = Array.isArray(loginWeddings?.items) && loginWeddings.items.length > 0;
  const hasSettingsPermission = menuPermissions && Object.values(menuPermissions.orgSettings).some(perm => perm);

  // Allow sync modal component to fire only if user has no login weddings.
  const showSyncModal = !!userInfo && loginWeddings && loginWeddings.length === 0;

  const { getUserInfo } = bindActionCreators(actions, dispatch);

  const goToBilling = () => {
    window.location.href = '/app/v2/account/billing';
  };

  const goToLogin = () => {
    window.location.href = redirectUrl;
  };

  const ExpiredButton = (
    <button
      className="button"
      onClick={goToBilling}
    >
      Subscribe Today
    </button>
  );

  const ReSubscribeButton = (
    <button
      className="button"
      onClick={isOrgOwner ? goToBilling : () => {
        dispatch({ type: SET_SHOW_PAUSED_MODAL, payload: false });
      }}
    >
      {isOrgOwner ? 'Re-Subscribe Today' : 'Got It'}
    </button>
  );

  const GotItButton = (
    <button
      className="button"
      onClick={goToLogin}
    >
      Got it
    </button>
  );

  // Load initial user data
  useEffect(() => {
    getUserInfo();
  }, []);

  useEffect(() => {
    if (userInfo && process.env.REACT_APP_AP_INTERCOM_ENABLED) {
      // If loading the email editor in iframe, don't launch intercom.
      if (window.location.pathname.includes('/app/v2/leads-email') && window.location.pathname.includes('/frame')) {
        return;
      }
      const { id, firstName, email, orgId, features } = userInfo || {};
      const intercomEnabled = process.env.REACT_APP_AP_INTERCOM_ENABLED;
      const intercomAppId = process.env.REACT_APP_AP_INTERCOM_APP_ID;

      try {
        const intercomSettings = {
          ... intercomAppId && { app_id: intercomAppId },
          ... id && { user_id: `${id}` },
          ... firstName && { name: firstName },
          ... email && { email },
          ... orgId && { company: orgId },
        };

        // #TODO: This should come from the graphql data. The backend has a method can_use_intercom that can
        // send a boolean value here instead of duplicating this logic.
        if (intercomEnabled && ((features && features.includes('SUPPORT')) || orgId)) {
          window.Intercom('boot', intercomSettings);
        }
      } catch (e) {
        // #TODO: Implement error handling.
        // console.log('did not boot intercom', e);
      }
    }
  }, [userInfo]);

  const hasUncaughtError = userInfoErrors.length > 0 && userInfoErrors.every(err => !err.field);

  if (isForbidden) {
    return (
      <Container className="App">
        <Router>
          <Header />
          <ForbiddenModal />
          <Routes />
        </Router>
      </Container>
    );
  }

  if (isAuthenticated && hasUncaughtError) {
    return (
      <Container className="App">
        <Router>
          <Header />
          <APModal
            open
            title="Oops!"
          >
            <p>
              Something strange happened. Please try refreshing or contact support.
            </p>
          </APModal>
          <Routes />
        </Router>
      </Container>
    );
  }

  if (isAuthenticated && !userInfoLoaded) {
    return (
      <Container className="App">
        <Loading />
      </Container>
    );
  }

  if (!isAuthenticated && (window.location.pathname.includes('/app/v2/account/billing'))) {
    window.location.href = `${redirectUrl}?return=billing`;
    return (
      <Container className="App">
        <Loading />
      </Container>
    );
  }

  // Allow email editor to be in an iframe without rendering unecessary things.
  if (window.location.pathname.includes('/app/v2/leads-email') && window.location.pathname.includes('/frame')) {
    return (
      <Container className="App">
        <Router>
          <Route path="/app/v2/leads-emailer/:orgId/:leadId/:mode/"
            component={LeadsEmailer}
          />
        </Router>
      </Container>
    );
  }

  return (
    <Container className="App">
      <Suspense fallback={<Loading />}>
        <Router>
          {!(window.location.pathname.includes('/app/v2/our-wedding') ||
          window.location.pathname.includes('/app/v2/event')) && <Header />
          }
          <div className="header-spacer" />
          <SiteNotificationContainer />
          <SiteErrorContainer />
          {showSyncModal && (
            <SyncModal
              userId={userId}
            />
          )}
          <APModal
            open={(isTrialEnded || isPromoEnded) && !window.location.pathname.startsWith('/app/v2/account')}
            name={`${isPromoEnded ? 'Promo' : 'Trial'} Expired`}
            hasHeader={false}
            title={`Your ${isPromoEnded ? 'Promotional Period' : '30-Day Trial'} Has Ended`}
            onClose={goToBilling}
            actions={ExpiredButton}
          >
            <p>
              Now that you’ve had a chance to explore everything Aisle Planner has to offer,
              it’s time to choose a plan that works best for you.
            </p>
            <br />
            <p>
              There are different monthly and annual plans to choose from based on the number
              of active projects you will be working within at any time.
            </p>
          </APModal>
          <APModal
            open={!!showPausedModal}
            name="Account Paused"
            hasHeader={false}
            title={isOrgOwner ? 'Your account has been paused.' : 'Hello there!'}
            onClose={() => dispatch({ type: SET_SHOW_PAUSED_MODAL, payload: false })}
            actions={ReSubscribeButton}
          >
            {isOrgOwner ? (
              <p>
                Your account is currently on a 90-day pause, and your subscription will resume automatically
                once the pause duration is over. If you’d like to reactivate your account or cancel your
                subscription, please visit your subscriptions and billing page
                <a href='/app/v2/account/billing'><span>here</span></a>.
              </p>

            ) : (
              <p>
                It looks like your account isn’t currently linked to an organization or project in Aisle Planner.
                Not to worry, this is usually a quick fix!
                Reach out to the person who invited you into their account for assistance,
                but if that doesn’t do the trick, contact us at customer support{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://help.aisleplanner.com/en/articles/2126011-contact-us"
                >
                  <span>here</span>
                </a>
                .
              </p>
            )}
          </APModal>
          <APModal
            open={!isAuthenticated}
            hasHeader
            title="Welcome back"
            onClose={goToLogin}
            actions={GotItButton}
          >
            <p>
              We noticed you’ve been inactive for a while. To keep your data secure, we’ve logged you out.
              Please log back in through the button below.
            </p>
          </APModal>
          <Routes
            isPaused={isUserPaused}
            hasUnpausedProjects={hasUnpausedProjects}
            isOrgOwner={isOrgOwner}
            isDelinquent={isDelinquent}
            isChurned={isChurned}
            // Account is disabled when trial/promo expires. But we want to know
            // if account was canceled from churn or from user action.
            isAccountCanceled={(isCancel || isDisabled) && !(isTrial || isPromo)}
            isAdmin={isAdmin}
            isIntegrationsEnabled={isIntegrationsEnabled}
            hasSettingsPermission={hasSettingsPermission}
            hasActiveWeddings={hasActiveWeddings}
          />
        </Router>
      </Suspense>
    </Container>
  );
};

export default App;
